import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Button, Icon } from 'antd'
import './CopyImageModal.scss'

const CopyImageModal = ({
  visible,
  sourceLabel,
  sourceImageSrc,
  targets,
  onCancel,
  onConfirm
}) => {
  const [selectedTarget, setSelectedTarget] = useState(null)
  const [step, setStep] = useState('select') // 'select' | 'confirm'

  const selectableTargets = useMemo(
    () => targets.filter(target => !target.isGenerating),
    [targets]
  )

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!visible) {
      setSelectedTarget(null)
      setStep('select')
    }
  }, [visible])

  const selectedTargetData = useMemo(() => {
    if (!selectedTarget) return null
    return selectableTargets.find(t => t.value === selectedTarget) || null
  }, [selectedTarget, selectableTargets])

  const handleSelectTarget = value => {
    setSelectedTarget(value)
  }

  const handleProceedToConfirm = () => {
    if (!selectedTargetData) return
    setStep('confirm')
  }

  const handleBackToSelect = () => {
    setStep('select')
  }

  const handleConfirm = () => {
    onConfirm(selectedTarget)
  }

  const renderTargetCard = target => {
    const isSelected = selectedTarget === target.value
    return (
      <div
        key={target.value}
        className={`copy-image__target-card${
          isSelected ? ' copy-image__target-card--selected' : ''
        }`}
        onClick={() => handleSelectTarget(target.value)}
      >
        <div className="copy-image__target-thumb-wrap">
          {target.imageSrc ? (
            <>
              <img
                src={target.imageSrc}
                alt={target.label}
                className="copy-image__target-thumb"
              />
              <span className="copy-image__target-badge copy-image__target-badge--has-image">
                Has image
              </span>
            </>
          ) : (
            <>
              <div className="copy-image__target-empty">
                <Icon type="picture" />
                <span>No image</span>
              </div>
              <span className="copy-image__target-badge copy-image__target-badge--empty">
                Empty
              </span>
            </>
          )}
          {isSelected && (
            <span className="copy-image__selected-check">
              <Icon type="check" />
            </span>
          )}
        </div>
        <div className="copy-image__target-label">
          <span className="copy-image__target-scene">
            {target.sceneTitle}
          </span>
          <span className="copy-image__target-shot">
            Shot {target.shotIndex}
            {target.shotTitle ? ` — ${target.shotTitle}` : ''}
          </span>
        </div>
      </div>
    )
  }

  const renderSelectStep = () => (
    <>
      <div className="copy-image__body">
        {/* Header */}
        <div className="copy-image__header">
          <div className="copy-image__header-icon">
            <Icon type="copy" />
          </div>
          <div className="copy-image__header-copy">
            <h2>Copy Image to Shot</h2>
            <p>Select a target shot to receive this image</p>
          </div>
        </div>

        {/* Source preview strip */}
        <div className="copy-image__source">
          {sourceImageSrc && (
            <img
              src={sourceImageSrc}
              alt="Source"
              className="copy-image__source-thumb"
            />
          )}
          <div className="copy-image__source-info">
            <span className="copy-image__source-eyebrow">Copying from</span>
            <span className="copy-image__source-name">
              {sourceLabel || 'Current shot'}
            </span>
          </div>
        </div>

        {/* Target grid */}
        <div className="copy-image__section-label">
          Available shots <span>({selectableTargets.length})</span>
        </div>

        {selectableTargets.length > 0 ? (
          <div className="copy-image__target-grid">
            {selectableTargets.map(renderTargetCard)}
          </div>
        ) : (
          <div className="copy-image__empty">
            <Icon type="inbox" />
            <p>No available shots to copy to</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="copy-image__footer">
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          type="primary"
          disabled={!selectedTarget}
          onClick={handleProceedToConfirm}
        >
          Copy to this shot <Icon type="arrow-right" />
        </Button>
      </div>
    </>
  )

  const renderConfirmStep = () => {
    const targetHasImage = !!selectedTargetData?.imageSrc
    const targetLabel = selectedTargetData
      ? `${selectedTargetData.sceneTitle} — Shot ${selectedTargetData.shotIndex}${
          selectedTargetData.shotTitle
            ? ` (${selectedTargetData.shotTitle})`
            : ''
        }`
      : ''

    return (
      <div className="copy-image__confirm">
        <div className="copy-image__body">
          {/* Header */}
          <div className="copy-image__header">
            <div className="copy-image__header-icon">
              <Icon type="swap" />
            </div>
            <div className="copy-image__header-copy">
              <h2>Confirm Image Copy</h2>
              <p>Review the change before applying</p>
            </div>
          </div>

          {/* Side-by-side comparison */}
          <div className="copy-image__compare">
            {/* Before — target's current state */}
            <div className="copy-image__compare-card copy-image__compare-card--target">
              <div className="copy-image__compare-img-wrap">
                {targetHasImage ? (
                  <img
                    src={selectedTargetData.imageSrc}
                    alt="Current"
                    className="copy-image__compare-img"
                  />
                ) : (
                  <div className="copy-image__compare-empty">
                    <Icon type="picture" />
                    <span>No image yet</span>
                  </div>
                )}
              </div>
              <div className="copy-image__compare-info">
                <span className="copy-image__compare-eyebrow copy-image__compare-eyebrow--before">
                  Before
                </span>
                <span className="copy-image__compare-name">{targetLabel}</span>
              </div>
            </div>

            {/* Arrow */}
            <div className="copy-image__compare-arrow">
              <Icon type="arrow-right" />
            </div>

            {/* After — what it will look like (the source image) */}
            <div className="copy-image__compare-card copy-image__compare-card--source">
              <div className="copy-image__compare-img-wrap">
                {sourceImageSrc ? (
                  <img
                    src={sourceImageSrc}
                    alt="After"
                    className="copy-image__compare-img"
                  />
                ) : (
                  <div className="copy-image__compare-empty">
                    <Icon type="picture" />
                    <span>No image</span>
                  </div>
                )}
              </div>
              <div className="copy-image__compare-info">
                <span className="copy-image__compare-eyebrow copy-image__compare-eyebrow--after">
                  After
                </span>
                <span className="copy-image__compare-name">{targetLabel}</span>
              </div>
            </div>
          </div>

          {/* Callout */}
          {targetHasImage ? (
            <div className="copy-image__callout copy-image__callout--warning">
              <div className="copy-image__callout-icon">
                <Icon type="exclamation-circle" />
              </div>
              <div className="copy-image__callout-text">
                <strong>This will replace the existing image</strong>
                <p>
                  The current image in this shot will be moved to history and
                  replaced with the source image.
                </p>
              </div>
            </div>
          ) : (
            <div className="copy-image__callout copy-image__callout--info">
              <div className="copy-image__callout-icon">
                <Icon type="info-circle" />
              </div>
              <div className="copy-image__callout-text">
                <strong>This shot has no image yet</strong>
                <p>
                  The source image will be added to this empty shot.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="copy-image__footer">
          <Button onClick={handleBackToSelect}>
            <Icon type="arrow-left" /> Back
          </Button>
          <Button type="primary" onClick={handleConfirm}>
            Confirm Copy
          </Button>
        </div>
      </div>
    )
  }

  return (
    <Modal
      visible={visible}
      footer={null}
      width={step === 'confirm' ? 620 : 580}
      maskClosable
      destroyOnClose
      centered
      bodyStyle={{ padding: 0 }}
      wrapClassName="copy-image-modal-wrap"
      onCancel={onCancel}
    >
      {step === 'select' ? renderSelectStep() : renderConfirmStep()}
    </Modal>
  )
}

export default CopyImageModal
